import React, { useState, useEffect } from "react";
import "./constants/styles/app.scss";
import PhysicianPage from "./physician/PhysicianPage";
import PatientSummary from "./physician/PatientSummary";
import Header from "./patient/containers/Header";
import CurrentSession from "./patient/containers/CurrentSession";
import PreSession from "./patient/containers/PreSession";
import FAQ from "./patient/containers/FAQ";
import "@fontsource/outfit";
import "@fontsource/inter";
import PostSession from "./patient/containers/PostSession";
import Landing from "./Landing";
import './constants/styles/landing.scss';

function App() {
    const [currentPage, setCurrentPage] = useState('landing');
    const [surveyCompleted, setSurveyCompleted] = useState(false);
    const [summaryData, setSummaryData] = useState(null);
    const [activePanel, setActivePanelState] = useState("PreSession");
    const [chatEnded, setChatEnded] = useState(false);
    const [isEndChatButtonDisabled, setIsEndChatButtonDisabled] = useState(false);
    const [chatDetails, setChatDetails] = useState({
        chatStarted: false,
        chatEnded: false,
        conversationId: null,
        initialBotMessage: null,
        conversationHistory: null,
    });
    const [previousPage, setPreviousPage] = useState(null);
    const [hashedInput, setHashedInput] = useState("");
    const [userId, setUserId] = useState("");
    const handleUserId = (id) => {
        setUserId(id);
    };

    useEffect(() => {
        document.title = "Intake Bot";
    }, []);

    const setActivePanel = (newPanel) => {
        setActivePanelState(newPanel);
    };

    useEffect(() => {
        document.title = "Intake Bot";

        const handlePopState = () => {
            if (previousPage) {
                setCurrentPage(previousPage);
            } else {
                setCurrentPage('landing');
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [previousPage]);

    const navigateTo = (page) => {
        setPreviousPage(currentPage);
        setCurrentPage(page);
        window.history.pushState(null, '', page);
    };

    const startChat = () => {
        setIsEndChatButtonDisabled(false);
        fetch(
            "https://www.cs.toronto.edu/~brli/intakebot/api/generateID", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({user:hashedInput}),
            }
        )
            .then((response) => response.json())
            .then((data) => {
                setChatDetails({
                    chatStarted: true,
                    conversationId: data.ID,
                    initialBotMessage: data.result,
                    chatEnded: false,
                });
            })
            .catch((error) => {
                console.error("Error fetching conversation ID:", error);
            });
    };

    const updateSummaryData = (editedSummary) => {
        setSummaryData(editedSummary);
    };

    const endChat = () => {
        setIsEndChatButtonDisabled(true);
        if (chatDetails.conversationId) {
            fetch("https://www.cs.toronto.edu/~brli/intakebot/api/summary", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ ID: chatDetails.conversationId }),
            })
                .then((response) => response.json())
                .then((data) => {
                    setSummaryData(data.result);
                    return fetch(
                        "https://www.cs.toronto.edu/~brli/intakebot/api/conversationHistory",
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                ID: chatDetails.conversationId,
                            }),
                        }
                    );
                })
                .then((response) => response.json())
                .then((data) => {
                    setChatDetails((prevDetails) => ({
                        ...prevDetails,
                        chatEnded: true,
                        conversationHistory: data,
                    }));
                })
                .catch((error) => {
                    console.error("Error:", error);
                    setChatDetails((prevDetails) => ({
                        ...prevDetails,
                        chatEnded: true,
                    }));
                });
        } else {
            console.error("No conversation ID found.");
            setChatDetails((prevDetails) => ({
                ...prevDetails,
                chatEnded: true,
            }));
        }
    };

    const fetchEditedSummary = () => {
        if (chatDetails.conversationId) {
            fetch("https://www.cs.toronto.edu/~brli/intakebot/api/summaryEdited", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ ID: chatDetails.conversationId }),
            })
                .then((response) => response.json())
                .then((data) => {
                    setSummaryData(data.result);
                })
                .catch((error) => {
                    console.error("Error fetching edited summary:", error);
                });
        }
    };

    const restartChat = () => {
        setChatDetails({
            chatStarted: false,
            chatEnded: false,
            conversationId: null,
            initialBotMessage: null,
        });
        setActivePanel("Current Session");
    };

    const deleteUser = (conversationId) => {
        fetch("https://www.cs.toronto.edu/~brli/intakebot/api/deleteID", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ ID: conversationId }),
        })
            .then((response) => response.json())
            .then((data) => {
                console.log("User deleted:", data);
            })
            .catch((error) => {
                console.error("Error deleting user:", error);
            });
    };

    const renderPage = () => {
        switch (currentPage) {
            case 'landing':
                return <Landing navigateTo={navigateTo} />;
            case 'patient':
                return (
                    <>
                        <Header
                            setActivePanel={setActivePanel}
                            activePanel={activePanel}
                            endChat={endChat}
                            chatEnded={chatDetails.chatEnded}
                            chatStarted={chatDetails.chatStarted}
                            restartChat={restartChat}
                            userId={userId}
                            isEndChatButtonDisabled={isEndChatButtonDisabled}
                            deleteUser={deleteUser}
                            hashedInput={hashedInput}
                        />
                        <div className='container'>
                            {activePanel === "PreSession" && (
                                <PreSession
                                    setActivePanel={setActivePanel}
                                    startChat={startChat}
                                    surveyCompleted={surveyCompleted}
                                    setSurveyCompleted={setSurveyCompleted}
                                    setHashedInput={setHashedInput}
                                    setUserId={handleUserId}
                                />
                            )}
                            {activePanel === "Current Session" && (
                                <CurrentSession
                                    chatDetails={chatDetails}
                                    endChat={endChat}
                                    summaryData={summaryData}
                                    updateSummaryData={updateSummaryData}
                                    setActivePanel={setActivePanel}
                                    hashedInput={hashedInput}
                                    userId={userId}
                                />
                            )}
                            {activePanel === "FAQ" && <FAQ />}
                            {activePanel === "PostSession" && (
                                <PostSession setActivePanel={setActivePanel} />
                            )}
                        </div>
                    </>
                );
            case 'clinician':
                return <PhysicianPage />;
            default:
                return <Landing navigateTo={navigateTo} />;
        }
    };

    return <>{renderPage()}</>;
}

export default App;
